import { gql } from 'apollo-boost'

export const PHOTO_FIELDS = gql`
  fragment PhotoFields on Photo {
    id
    fileId
    inUse
    status
    capturedAt
    timeSource
    createdAt
    latitude
    longitude
    heading
    device
    width
    height
    title
    description
    building { id name }
    floor { id name }
    trade
    folder { id name isInbox system }
    visibility
    uploadedBy { id name }
    tags { id projectId name color }
    albums { id name }
    thumbUrl
    displayUrl
    originalUrl
    filename
    canEdit
    canDelete
    canReprocess
  }
`

const FOLDER_FIELDS = `id projectId parentId name sortOrder isInbox system photoCount canDelete createdBy { id name }`
const ALBUM_FIELDS = `id projectId name sortOrder photoCount coverUrl canEdit canDelete createdBy { id name }`
const SHARE_FIELDS = `id kind status createdAt createdBy { id name } photoCount recipientUsers { id name } recipientEmails message expiresAt hasPassword viewCount downloadCount lastAccessedAt revokedAt url canRevoke`
const TAG_FIELDS = `id projectId name color usageCount createdBy { id name }`

export const PHOTOS = gql`
  query photos($projectId: ID!, $filter: PhotoFilter, $after: String, $first: Int) {
    photos(projectId: $projectId, filter: $filter, after: $after, first: $first) {
      nodes { ...PhotoFields }
      nextCursor
      totalCount
    }
  }
  ${PHOTO_FIELDS}
`

export const PHOTO = gql`
  query photo($id: ID!) {
    photo(id: $id) { ...PhotoFields }
  }
  ${PHOTO_FIELDS}
`

export const PHOTO_ACTIVITY = gql`
  query photoActivity($photoId: ID!) {
    photoActivity(photoId: $photoId) { id action details createdAt user { id name } }
  }
`

export const PHOTO_FOLDERS = gql`
  query photoFolders($projectId: ID!) {
    photoFolders(projectId: $projectId) { ${FOLDER_FIELDS} }
  }
`

export const PHOTO_ALBUMS = gql`
  query photoAlbums($projectId: ID!) {
    photoAlbums(projectId: $projectId) { ${ALBUM_FIELDS} }
  }
`

export const PHOTO_TAGS = gql`
  query photoTags($projectId: ID!) {
    photoTags(projectId: $projectId) { ${TAG_FIELDS} }
  }
`

export const REGISTER_PHOTOS = gql`
  mutation registerPhotos($projectId: ID!, $fileIds: [ID!]!, $input: PhotoBatchInput) {
    registerPhotos(projectId: $projectId, fileIds: $fileIds, input: $input) { id }
  }
`

export const UPDATE_PHOTO = gql`
  mutation updatePhoto($id: ID!, $input: PhotoUpdateInput!) {
    updatePhoto(id: $id, input: $input) { ...PhotoFields }
  }
  ${PHOTO_FIELDS}
`

export const UPDATE_PHOTOS = gql`
  mutation updatePhotos($ids: [ID!]!, $input: PhotoBulkInput!) {
    updatePhotos(ids: $ids, input: $input) { ...PhotoFields }
  }
  ${PHOTO_FIELDS}
`

export const DELETE_PHOTOS = gql`
  mutation deletePhotos($ids: [ID!]!) {
    deletePhotos(ids: $ids)
  }
`

export const REPROCESS_PHOTO = gql`
  mutation reprocessPhoto($id: ID!) {
    reprocessPhoto(id: $id) { ...PhotoFields }
  }
  ${PHOTO_FIELDS}
`

export const CREATE_PHOTO_FOLDER = gql`
  mutation createPhotoFolder($projectId: ID!, $parentId: ID, $name: String!) {
    createPhotoFolder(projectId: $projectId, parentId: $parentId, name: $name) { ${FOLDER_FIELDS} }
  }
`

export const RENAME_PHOTO_FOLDER = gql`
  mutation renamePhotoFolder($id: ID!, $name: String!) {
    renamePhotoFolder(id: $id, name: $name) { ${FOLDER_FIELDS} }
  }
`

export const MOVE_PHOTO_FOLDER = gql`
  mutation movePhotoFolder($id: ID!, $parentId: ID, $sortOrder: Int) {
    movePhotoFolder(id: $id, parentId: $parentId, sortOrder: $sortOrder) { ${FOLDER_FIELDS} }
  }
`

export const DELETE_PHOTO_FOLDER = gql`
  mutation deletePhotoFolder($id: ID!, $targetFolderId: ID) {
    deletePhotoFolder(id: $id, targetFolderId: $targetFolderId)
  }
`

export const CREATE_PHOTO_ALBUM = gql`
  mutation createPhotoAlbum($projectId: ID!, $name: String!) {
    createPhotoAlbum(projectId: $projectId, name: $name) { ${ALBUM_FIELDS} }
  }
`

export const UPDATE_PHOTO_ALBUM = gql`
  mutation updatePhotoAlbum($id: ID!, $name: String, $coverPhotoId: ID) {
    updatePhotoAlbum(id: $id, name: $name, coverPhotoId: $coverPhotoId) { ${ALBUM_FIELDS} }
  }
`

export const DELETE_PHOTO_ALBUM = gql`
  mutation deletePhotoAlbum($id: ID!) {
    deletePhotoAlbum(id: $id)
  }
`

export const ADD_PHOTOS_TO_ALBUM = gql`
  mutation addPhotosToAlbum($albumId: ID!, $photoIds: [ID!]!) {
    addPhotosToAlbum(albumId: $albumId, photoIds: $photoIds) { ${ALBUM_FIELDS} }
  }
`

export const REMOVE_PHOTOS_FROM_ALBUM = gql`
  mutation removePhotosFromAlbum($albumId: ID!, $photoIds: [ID!]!) {
    removePhotosFromAlbum(albumId: $albumId, photoIds: $photoIds) { ${ALBUM_FIELDS} }
  }
`

export const REORDER_ALBUM_PHOTOS = gql`
  mutation reorderAlbumPhotos($albumId: ID!, $photoIds: [ID!]!) {
    reorderAlbumPhotos(albumId: $albumId, photoIds: $photoIds) { ${ALBUM_FIELDS} }
  }
`

export const CREATE_PHOTO_TAG = gql`
  mutation createPhotoTag($projectId: ID!, $name: String!, $color: String!) {
    createPhotoTag(projectId: $projectId, name: $name, color: $color) { ${TAG_FIELDS} }
  }
`

export const UPDATE_PHOTO_TAG = gql`
  mutation updatePhotoTag($id: ID!, $name: String, $color: String) {
    updatePhotoTag(id: $id, name: $name, color: $color) { ${TAG_FIELDS} }
  }
`

export const MERGE_PHOTO_TAGS = gql`
  mutation mergePhotoTags($sourceIds: [ID!]!, $targetId: ID!) {
    mergePhotoTags(sourceIds: $sourceIds, targetId: $targetId) { ${TAG_FIELDS} }
  }
`

export const DELETE_PHOTO_TAGS = gql`
  mutation deletePhotoTags($ids: [ID!]!) {
    deletePhotoTags(ids: $ids)
  }
`

export const CREATE_PHOTO_REPORT = gql`
  mutation createPhotoReport($projectId: ID!, $input: PhotoReportInput!) {
    createPhotoReport(projectId: $projectId, input: $input) { id type status progress error pdfUrl }
  }
`

export const CREATE_SNAG_FROM_PHOTOS = gql`
  mutation createSnagFromPhotos($photoIds: [ID!]!, $input: CreateSnagInput!) {
    createSnagFromPhotos(photoIds: $photoIds, input: $input) { id }
  }
`

export const PHOTO_REFERENCES = gql`
  query photoReferences($id: ID!) {
    photo(id: $id) { id references { kind id label meetingId snagNumber } }
  }
`

export const ATTACH_PHOTOS_TO_SNAG = gql`
  mutation attachPhotosToSnag($snagId: ID!, $photoIds: [ID!]!) {
    attachPhotosToSnag(snagId: $snagId, photoIds: $photoIds)
  }
`

export const PHOTO_SHARES = gql`
  query photoShares($projectId: ID!) {
    photoShares(projectId: $projectId) { ${SHARE_FIELDS} }
  }
`

export const PHOTO_SHARE_RECIPIENT_CHECK = gql`
  query photoShareRecipientCheck($projectId: ID!, $selection: PhotoShareSelection!, $userIds: [ID!]!) {
    photoShareRecipientCheck(projectId: $projectId, selection: $selection, userIds: $userIds) { user { id name } hiddenCount }
  }
`

export const SHARE_PHOTOS_INTERNALLY = gql`
  mutation sharePhotosInternally($projectId: ID!, $selection: PhotoShareSelection!, $userIds: [ID!]!, $message: String) {
    sharePhotosInternally(projectId: $projectId, selection: $selection, userIds: $userIds, message: $message) { ${SHARE_FIELDS} }
  }
`

export const CREATE_PHOTO_SHARE_LINK = gql`
  mutation createPhotoShareLink($projectId: ID!, $selection: PhotoShareSelection!, $expiresAt: DateTime!, $password: String, $emails: [String!], $message: String, $confirmInternal: Boolean) {
    createPhotoShareLink(projectId: $projectId, selection: $selection, expiresAt: $expiresAt, password: $password, emails: $emails, message: $message, confirmInternal: $confirmInternal) { ${SHARE_FIELDS} }
  }
`

export const REVOKE_PHOTO_SHARE = gql`
  mutation revokePhotoShare($id: ID!) {
    revokePhotoShare(id: $id) { ${SHARE_FIELDS} }
  }
`

export const ACCESS_PHOTO_SHARE = gql`
  query accessPhotoShare($token: String!, $password: String) {
    accessPhotoShare(token: $token, password: $password) { success error requiresPassword }
  }
`

export const SHARED_PHOTOS = gql`
  query sharedPhotos($token: String!) {
    sharedPhotos(token: $token) { projectName expiresAt photos { id thumbUrl displayUrl width height } }
  }
`
